/**
 * VAIR 5-Player Algorithm
 *
 * Based on official VAIR 5-Player Score Card v4.15.25
 *
 * Structure:
 * - 10 total games (2 rounds of 5 games each)
 * - 1 court (matches played sequentially)
 * - 1 player has a bye each game (rotating)
 * - Each player plays 8 games total (sits out 2 games)
 * - Partnerships repeat intentionally (for practice/exposure)
 *
 * Round 1: Games 1-5
 * Round 2: Games 6-10
 */
import { VairRound, VairSchedule } from './types';

/**
 * Generate VAIR schedule for 5 players
 * Based on official VAIR 5-Player Score Card
 */
export function generateVairSchedule5Players(timePerMatch: number = 20): VairSchedule {
  let currentTime = 0;
  const rounds: VairRound[] = [];

  // ROUND 1

  // Game 1: 1-2 vs 3-4 (Player 5 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        team2: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [4], // Player 5 (0-indexed: 4)
  });
  currentTime += timePerMatch;

  // Game 2: 2-3 vs 1-5 (Player 4 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        team2: { player1: 0, player2: 4 }, // Players 1-5 (0-indexed: 0, 4)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [3], // Player 4 (0-indexed: 3)
  });
  currentTime += timePerMatch;

  // Game 3: 1-4 vs 2-5 (Player 3 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        team2: { player1: 1, player2: 4 }, // Players 2-5 (0-indexed: 1, 4)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [2], // Player 3 (0-indexed: 2)
  });
  currentTime += timePerMatch;

  // Game 4: 4-5 vs 1-3 (Player 2 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 4 }, // Players 4-5 (0-indexed: 3, 4)
        team2: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [1], // Player 2 (0-indexed: 1)
  });
  currentTime += timePerMatch;

  // Game 5: 3-5 vs 2-4 (Player 1 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 4 }, // Players 3-5 (0-indexed: 2, 4)
        team2: { player1: 1, player2: 3 }, // Players 2-4 (0-indexed: 1, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0], // Player 1 (0-indexed: 0)
  });
  currentTime += timePerMatch;

  // ROUND 2

  // Game 6: 1-5 vs 2-3 (Player 4 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 4 }, // Players 1-5 (0-indexed: 0, 4)
        team2: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [3], // Player 4 (0-indexed: 3)
  });
  currentTime += timePerMatch;

  // Game 7: 1-2 vs 4-5 (Player 3 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        team2: { player1: 3, player2: 4 }, // Players 4-5 (0-indexed: 3, 4)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [2], // Player 3 (0-indexed: 2)
  });
  currentTime += timePerMatch;

  // Game 8: 3-5 vs 1-4 (Player 2 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 4 }, // Players 3-5 (0-indexed: 2, 4)
        team2: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [1], // Player 2 (0-indexed: 1)
  });
  currentTime += timePerMatch;

  // Game 9: 3-4 vs 2-5 (Player 1 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        team2: { player1: 1, player2: 4 }, // Players 2-5 (0-indexed: 1, 4)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0], // Player 1 (0-indexed: 0)
  });
  currentTime += timePerMatch;

  // Game 10: 2-4 vs 1-3 (Player 5 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 3 }, // Players 2-4 (0-indexed: 1, 3)
        team2: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [4], // Player 5 (0-indexed: 4)
  });

  return { rounds };
}

/**
 * Validate 5-player VAIR schedule
 * - Each player should play in exactly 8 games (sit out 2)
 * - 1 player should have a bye each game
 * - Total of 10 games
 */
export function validateVairSchedule5Players(schedule: VairSchedule): boolean {
  const playerGameCount: Map<number, number> = new Map();
  const playerByeCount: Map<number, number> = new Map();

  // Initialize
  for (let i = 0; i < 5; i++) {
    playerGameCount.set(i, 0);
    playerByeCount.set(i, 0);
  }

  // Count games and byes per player
  schedule.rounds.forEach((round) => {
    // Count active players
    round.matches.forEach((match) => {
      const allPlayers = [
        match.team1.player1,
        match.team1.player2,
        match.team2.player1,
        match.team2.player2,
      ];

      allPlayers.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
      });
    });

    // Count byes
    round.byes.forEach((player) => {
      playerByeCount.set(player, (playerByeCount.get(player) || 0) + 1);
    });
  });

  // Validate: Each player should play 8 games
  const expectedGames = 8;
  for (let i = 0; i < 5; i++) {
    const gamesPlayed = playerGameCount.get(i) || 0;
    if (gamesPlayed !== expectedGames) {
      console.error(
        `[5p VAIR] Player ${i + 1} played ${gamesPlayed} games, expected ${expectedGames}`,
      );
      return false;
    }
  }

  // Validate: Each player should have exactly 2 byes
  const expectedByes = 2;
  for (let i = 0; i < 5; i++) {
    const byes = playerByeCount.get(i) || 0;
    if (byes !== expectedByes) {
      console.error(`[5p VAIR] Player ${i + 1} had ${byes} byes, expected ${expectedByes}`);
      return false;
    }
  }

  // Validate: Should have 10 rounds total
  if (schedule.rounds.length !== 10) {
    console.error(`[5p VAIR] Expected 10 rounds, got ${schedule.rounds.length}`);
    return false;
  }

  // Validate: Each round should have exactly 1 match and 1 bye
  const invalidRounds = schedule.rounds.filter(
    (round) => round.matches.length !== 1 || round.byes.length !== 1,
  );
  if (invalidRounds.length > 0) {
    console.error(`[5p VAIR] Expected 1 match and 1 bye per round, found invalid rounds`);
    return false;
  }

  return true;
}
